/**
 * プロフィール欄(アバター + コイン + ガチャ/きせかえへの入口)。
 *
 * タイトルとステージ選択に置く。docs/02_game_design.md §1 の画面フローで
 * アバター・ガチャはステージ選択の横にぶら下がる。
 */

import { baseCharacterOf, summarizeModifiers } from '../../logic/avatar';
import { TITLE_KEY_BADGES, type TitleAction } from '../../logic/titleKeys';
import { useAuthStore } from '../../store/authStore';
import { selectEquippedModifiers, useGameStore } from '../../store/gameStore';
import { AvatarView } from './AvatarView';
import { KeyBadge } from './gameFeel';

interface Props {
  readonly compact?: boolean;
  /**
   * ボタンに数字キーのバッジを出す(docs/15_game_feel.md §2-A)。
   * キー操作を受けているのはタイトル画面だけなので、既定は出さない。
   */
  readonly keyHints?: boolean;
}

export function ProfileBar({ compact = false, keyHints = false }: Props) {
  const state = useGameStore();
  const effects = summarizeModifiers(selectEquippedModifiers(state));
  const base = baseCharacterOf(state.inventory.base);
  // 登録・ログイン(docs/10 §3.1)。ローカルモードでは導線ごと出さない。
  const authSupported = useAuthStore((auth) => auth.supported);
  const account = useAuthStore((auth) => auth.user);
  const signedIn = account !== null && !account.isAnonymous;

  /**
   * キーバッジ(`gameFeel` 共通の角丸キー様式)。中身は `aria-hidden` なので、
   * ボタンのアクセシブル名は文言のままで変わらない。
   */
  const badge = (action: TitleAction) =>
    keyHints ? <KeyBadge keyLabel={TITLE_KEY_BADGES[action]} className="on-dark" /> : null;

  return (
    <div className={`profile-bar ${compact ? 'compact' : ''}`}>
      <AvatarView
        catalog={state.catalog}
        inventory={state.inventory}
        size={compact ? 'sm' : 'md'}
      />
      <div className="profile-info">
        <p className="profile-name">{base.name}</p>
        <p className="coin-line">
          <span className="coin-icon">◎</span> {state.progress.coins} コイン
        </p>
        {effects.length > 0 ? (
          <ul className="profile-effects">
            {effects.map((effect) => (
              <li key={effect.type}>{effect.text}</li>
            ))}
          </ul>
        ) : (
          <p className="profile-effects none">そうびの こうかは まだ ないよ</p>
        )}
      </div>
      <div className="profile-actions">
        <button type="button" className="meta-button gacha" onClick={() => void state.goGacha()}>
          🎁 ガチャ
          {badge('gacha')}
        </button>
        <button type="button" className="meta-button avatar" onClick={() => void state.goAvatar()}>
          👕 きせかえ
          {badge('avatar')}
        </button>
        {/* 学習レポート(docs/07_learning_support.md §4.2 生徒ビュー)。 */}
        <button type="button" className="meta-button my-page" onClick={() => void state.goMyPage()}>
          📔 きろく
          {badge('myPage')}
        </button>
        {/* パルの ようす(docs/14_characters.md §2)。きろくの となりに置く。 */}
        <button type="button" className="meta-button pal" onClick={state.goPal}>
          🐣 パル
          {badge('pal')}
        </button>
        {/* とうろく・ログイン(docs/10 §3.1)。遊びの邪魔をしない控えめな入口。 */}
        {authSupported ? (
          <button type="button" className="meta-button account" onClick={() => state.goAccount()}>
            {signedIn ? '👪 アカウント' : '👪 とうろく'}
          </button>
        ) : null}
      </div>
    </div>
  );
}
